import { AsyncPipe } from '@angular/common';
import type { OnInit, Signal } from '@angular/core';
import { Component, Injector, effect, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import type { NonNullableFormBuilder } from '@angular/forms';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import type { OnyxCustomAddress, OnyxFormMode, OnyxPhone } from '@onyx/angular';
import {
  OnyxAddressInputComponent,
  OnyxAutoFocusDirective,
  OnyxDatepickerComponent,
  OnyxDropdownComponent,
  OnyxFormGroupComponent,
  OnyxIconComponent,
  OnyxLinkComponent,
  OnyxPhoneNumberInputComponent,
  OnyxSectionComponent,
  OnyxSuggestionsComponent,
  OnyxTextFieldComponent,
  PeselNumberHelper,
  onyxDateValidator,
  onyxEmailValidator,
  onyxMaxDateValidator,
  onyxPeselNumberValidator,
  onyxPhoneValidator,
  onyxTextFieldLengthValidator,
} from '@onyx/angular';
import { map, merge, startWith } from 'rxjs';
import type { Country } from '../../../../common/enums/country';
import { DictionaryCode } from '../../../../common/enums/dictionary-code';
import { SuggestionHelper } from '../../../../common/helpers/suggestions.helper';
import { DictionariesService } from '../../../../common/services/dictionaries.service';
import { IntercomService } from '../../../common/services/intercom.service';
import { BaseHelper } from '../../../management-panel/bases/common/helpers/base.helper';
import { DriverHelper } from '../../common/helpers/driver.helper';
import type { PrivatePhoneConflict } from '../../common/validators/unique-private-phone.validator';
import { uniquePrivatePhoneValidator } from '../../common/validators/unique-private-phone.validator';

type DriverDataFormGroup = ReturnType<
  (typeof DriverDataFormComponent)['buildForm']
>;

@Component({
  selector: 'app-driver-data-form',
  imports: [
    OnyxSectionComponent,
    ReactiveFormsModule,
    OnyxTextFieldComponent,
    OnyxDatepickerComponent,
    OnyxDropdownComponent,
    OnyxAddressInputComponent,
    AsyncPipe,
    OnyxSuggestionsComponent,
    OnyxFormGroupComponent,
    OnyxPhoneNumberInputComponent,
    OnyxIconComponent,
    OnyxLinkComponent,
    TranslatePipe,
    OnyxAutoFocusDirective,
  ],
  templateUrl: './driver-data-form.component.html',
  styleUrl: './driver-data-form.component.scss',
})
export class DriverDataFormComponent implements OnInit {
  private readonly dictionariesService = inject(DictionariesService);
  private readonly injector = inject(Injector);
  private readonly baseHelper = inject(BaseHelper);
  private readonly suggestionsHelper = inject(SuggestionHelper);
  private readonly driverHelper = inject(DriverHelper);
  private readonly intercomService = inject(IntercomService);
  private readonly translateService = inject(TranslateService);

  public readonly form = input.required<DriverDataFormGroup>();
  public readonly mode = input.required<OnyxFormMode>();
  public readonly driverUuid = input<string | null>(null);

  protected readonly I18N = 'drivers.driverForm.driverData';

  protected readonly baseSuggestions$ = this.suggestionsHelper.baseSuggestions$;
  protected readonly countries$ = this.dictionariesService.getDictionary(
    DictionaryCode.COUNTRY,
  );
  protected readonly BASES_SOURCE = this.baseHelper.getBasesSource();

  protected readonly baseDropdownActions = this.baseHelper.getDropdownActions(
    () => this.form().controls.assignedBase,
  );

  protected phoneNumberConflict!: Signal<PrivatePhoneConflict | null>;

  public ngOnInit(): void {
    const controls = this.form().controls;

    controls.privatePhone.setAsyncValidators(
      uniquePrivatePhoneValidator(this.injector, {
        excludeDriverUuid: () => this.driverUuid(),
      }),
    );
    controls.privatePhone.updateValueAndValidity({ emitEvent: false });

    this.phoneNumberConflict = toSignal(
      merge(
        controls.privatePhone.valueChanges,
        controls.privatePhone.statusChanges,
      ).pipe(
        startWith(null),
        map(
          () =>
            (controls.privatePhone.errors?.[
              'privatePhoneConflict'
            ] as PrivatePhoneConflict) ?? null,
        ),
      ),
      { initialValue: null, injector: this.injector },
    );

    const peselNumber = toSignal(controls.peselNumber.valueChanges, {
      injector: this.injector,
    });
    effect(
      () => {
        const birthDate = PeselNumberHelper.getBirthDate(peselNumber() ?? '');
        if (birthDate != null) controls.birthDate.setValue(birthDate);
      },
      { injector: this.injector },
    );
  }

  protected showDriver(driverUuid: string): void {
    this.driverHelper.openModal(driverUuid);
  }

  protected contactSupport(phoneNumber: string): void {
    this.intercomService.startConversation(
      this.translateService.instant(
        `${this.I18N}.globalConflictInitialMessage`,
        { phoneNumber },
      ),
    );
  }

  public static buildForm(fb: NonNullableFormBuilder) {
    return fb.group({
      firstName: fb.control<string | null>(null, [
        Validators.required,
        onyxTextFieldLengthValidator,
      ]),
      lastName: fb.control<string | null>(null, [
        Validators.required,
        onyxTextFieldLengthValidator,
      ]),
      birthDate: fb.control<string | null>(null, [
        onyxDateValidator,
        onyxMaxDateValidator(),
      ]),
      nationality: fb.control<Country | null>(null, [Validators.required]),
      privatePhone: fb.control<OnyxPhone | null>(null, [
        Validators.required,
        onyxPhoneValidator,
      ]),
      workPhone: fb.control<OnyxPhone | null>(null, [onyxPhoneValidator]),
      privateEmail: fb.control<string | null>(null, [onyxEmailValidator]),
      peselNumber: fb.control<string | null>(null, [onyxPeselNumberValidator]),
      homeAddress: fb.control<OnyxCustomAddress | null>(null, [
        Validators.required,
      ]),
      assignedBase: fb.control<string | null>(null),
    });
  }
}
